import React, { useState, useEffect } from 'react';
import { Button, Form, Card, Row, Col } from 'react-bootstrap';
import { FaSave, FaRegWindowClose, FaLockOpen } from 'react-icons/fa';
import InfoToggle from './Global/InfoToggle';
import { validateDniSoft } from '../utils/dniUtils';
import AddressAutocompleteInput from './Global/AddressAutocompleteInput';
import ConfirmModal from './Global/ConfirmModal';
import './ClientInfoForm.css';

const AUTOCOMPLETE_FIELDS = ['address', 'city', 'zipCode', 'state'];
const ALL_LOCKED = { address: true, city: true, zipCode: true, state: true };
const ALL_UNLOCKED = { address: false, city: false, zipCode: false, state: false };

const ClientInfoForm = ({ client, isEditing, handleSubmit, handleCancel, setIsDirty, showCancelButton = true, hideAddress = false }) => {

    const normalizeClient = (data) => {
        const defaultAddress = data?.addresses?.find(addr => addr.isDefault) || data?.addresses?.[0];
        const normalized = {
            dni: data?.dni ?? '',
            name: data?.name ?? '',
            lastName1: data?.lastName1 ?? '',
            lastName2: data?.lastName2 ?? '',
            email: data?.email ?? '',
            phone: data?.phone ?? '',
            phone2: data?.phone2 ?? '',
            dateOfBirth: data?.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
            address: defaultAddress?.address ?? data?.address ?? '',
            addressDetails: '',
            zipCode: defaultAddress?.zipCode ?? data?.zipCode ?? '',
            city: defaultAddress?.city ?? data?.city ?? '',
            district: defaultAddress?.district ?? data?.district ?? '',
            state: defaultAddress?.state ?? data?.state ?? '',
            country: defaultAddress?.country ?? data?.country ?? 'España',
        };
        if (data?.id) normalized.id = data.id;
        return normalized;
    };

    const [localClient, setLocalClient] = useState(() => normalizeClient(client));
    const [dniError, setDniError] = useState(null);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [lockedFields, setLockedFields] = useState(ALL_LOCKED);
    const [unlockPending, setUnlockPending] = useState(false);
    const [showOptionalFields, setShowOptionalFields] = useState(() => !!client?.id && client.id !== '');

    useEffect(() => {
        if (!isEditing || (client?.id !== localClient?.id)) {
            setLocalClient(normalizeClient(client));
        }
    }, [client, isEditing]);

    const handleSuggestionSelect = (s) => {
        const autoAddress = [s.street, s.houseNumber].filter(Boolean).join(' ');
        setSelectedSuggestion({ address: autoAddress, city: s.city ?? '', zipCode: s.postcode ?? '', state: s.state ?? '', latitude: s.latitude, longitude: s.longitude });
        setLockedFields(ALL_LOCKED);
        setLocalClient(prev => ({ ...prev, address: autoAddress, city: s.city ?? prev.city, zipCode: s.postcode ?? prev.zipCode, state: s.state ?? prev.state }));
        setIsDirty(true);
    };

    const confirmUnlock = () => {
        setLockedFields(ALL_UNLOCKED);
        setUnlockPending(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLocalClient(prev => ({ ...prev, [name]: value }));
        if (name === 'dni') setDniError(validateDniSoft(value));
        setIsDirty(true);
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setLocalClient(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
    };

    const convertDateToISO = (date) => {
        if (!date) return null;
        return date.includes('T') ? date.split('T')[0] : date;
    };

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        if (dniError) return;

        const clientData = {
            id: localClient.id,
            dni: localClient.dni,
            name: localClient.name,
            lastName1: localClient.lastName1,
            lastName2: localClient.lastName2,
            email: localClient.email,
            phone: localClient.phone,
            phone2: localClient.phone2,
            dateOfBirth: convertDateToISO(localClient.dateOfBirth),
        };

        const geoMatches = selectedSuggestion && AUTOCOMPLETE_FIELDS.every(
            f => (localClient[f] ?? '').trim() === (selectedSuggestion[f] ?? '').trim()
        );

        const addressData = {
            clientId: localClient.id,
            address: [localClient.address, localClient.addressDetails].filter(Boolean).join(', '),
            zipCode: localClient.zipCode,
            city: localClient.city,
            district: localClient.district,
            state: localClient.state,
            country: localClient.country,
            isDefault: true,
            ...(geoMatches && { latitude: selectedSuggestion.latitude, longitude: selectedSuggestion.longitude, exactGeoMatch: true }),
        };

        try {
            await handleSubmit(clientData, addressData);
        } catch (error) {
            console.error('Error al guardar cliente y dirección:', error);
        }
    };

    return (
        <>
        <ConfirmModal
            show={unlockPending}
            onCancel={() => setUnlockPending(false)}
            onConfirm={confirmUnlock}
            title="Rellenar dirección a mano"
            body={<>Si rellenas los campos manualmente, <strong>se perderá la geolocalización</strong> asociada a la dirección autocompletada. Se recomienda usar el buscador siempre que sea posible.</>}
            confirmLabel="Rellenar a mano"
            cancelLabel="Cancelar"
            confirmVariant="warning"
        />
        <Card className="shadow-lg border-0">
            <Card.Header className="bg-primary text-white text-center py-3">
                <h4 className="mb-0">Información del Cliente</h4>
            </Card.Header>
            <Card.Body className="p-4">
                <form onSubmit={handleSubmitForm}>

                    {/* Datos personales */}
                    <div className="mb-4">
                        <h5 className="text-muted mb-3 border-bottom pb-2">Datos Personales</h5>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formName" className="mb-3">
                                    <Form.Label className="fw-semibold">Nombre *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={localClient.name}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        required
                                        placeholder="Introduce el nombre"
                                        className="form-control-lg"
                                        style={{ '::placeholder': { color: '#adb5bd', opacity: 0.7 } }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="formPhone" className="mb-3">
                                    <Form.Label className="fw-semibold">Teléfono *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={localClient.phone}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        required
                                        placeholder="Ej: 612 345 678"
                                        className="form-control-lg"
                                        style={{ '::placeholder': { color: '#adb5bd', opacity: 0.7 } }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formLastName1" className="mb-3">
                                    <Form.Label className="fw-semibold">Primer Apellido *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName1"
                                        value={localClient.lastName1}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        required
                                        placeholder="Primer apellido"
                                        className="form-control-lg"
                                        style={{ '::placeholder': { color: '#adb5bd', opacity: 0.7 } }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="formLastName2" className="mb-3">
                                    <Form.Label className="fw-semibold">Segundo Apellido</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName2"
                                        value={localClient.lastName2}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        placeholder="Segundo apellido (opcional)"
                                        className="form-control-lg"
                                        style={{ '::placeholder': { color: '#adb5bd', opacity: 0.7 } }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formPhone2" className="mb-3">
                                    <Form.Label className="fw-semibold">Teléfono Alternativo</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone2"
                                        value={localClient.phone2}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        placeholder="Ej: 987 654 321"
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="formEmail" className="mb-3">
                                    <Form.Label className="fw-semibold">Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={localClient.email}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        placeholder="Ej: cliente@email.com"
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* Dirección */}
                    {!hideAddress && (
                    <div className="mb-4">
                        <h5 className="text-muted mb-3 border-bottom pb-2">Dirección de envío</h5>
                        {!localClient.id && (
                            <>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-semibold">Buscar dirección</Form.Label>
                                    <AddressAutocompleteInput disabled={!isEditing} onSelect={handleSuggestionSelect} />
                                </Form.Group>
                                {isEditing && (
                                    <div className="mb-3">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 text-secondary"
                                            onClick={() => setUnlockPending(true)}
                                            data-testid="fill-manually-button"
                                        >
                                            <FaLockOpen className="me-1" />Rellenar a mano
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                        <Row>
                            <Col md={4}>
                                <Form.Group controlId="formZipCode" className="mb-3">
                                    <Form.Label className="fw-semibold">Código Postal *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="zipCode"
                                        value={localClient.zipCode}
                                        onChange={handleChange}
                                        disabled={!isEditing || lockedFields.zipCode}
                                        placeholder="Ej: 28001"
                                        required
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={8}>
                                <Form.Group controlId="formCity" className="mb-3">
                                    <Form.Label className="fw-semibold">Ciudad *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="city"
                                        value={localClient.city}
                                        onChange={handleChange}
                                        disabled={!isEditing || lockedFields.city}
                                        placeholder="Ej: Madrid"
                                        required
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group controlId="formAddress" className="mb-3">
                                    <Form.Label className="fw-semibold">Dirección *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="address"
                                        value={localClient.address}
                                        onChange={handleChange}
                                        disabled={!isEditing || lockedFields.address}
                                        placeholder="Ej: Calle Gran Vía, 123"
                                        required
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group controlId="formAddressDetails" className="mb-3">
                                    <Form.Label className="fw-semibold">Piso, puerta, escalera...</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="addressDetails"
                                        value={localClient.addressDetails}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        placeholder="Ej: 2º A, Escalera B (opcional)"
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formDistrict" className="mb-3">
                                    <Form.Label className="fw-semibold">Barrio</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="district"
                                        value={localClient.district}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        placeholder="Ej: Centro (opcional)"
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="formState" className="mb-3">
                                    <Form.Label className="fw-semibold">Provincia *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="state"
                                        value={localClient.state}
                                        onChange={handleChange}
                                        disabled={!isEditing || lockedFields.state}
                                        placeholder="Ej: Madrid"
                                        required
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formCountry" className="mb-3">
                                    <Form.Label className="fw-semibold">País *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="country"
                                        value={localClient.country}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        placeholder="Ej: España"
                                        required
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                    )}

                    {/* Información adicional */}
                    <div className="mb-4">
                        <InfoToggle
                            label="Información Adicional"
                            openState={showOptionalFields}
                            handleToggle={() => setShowOptionalFields(prev => !prev)}
                            color="#6c757d"
                        />
                    </div>

                    {showOptionalFields && (
                        <div className="mb-4">
                            <h5 className="text-muted mb-3 border-bottom pb-2">Datos Adicionales</h5>
                            <Row>
                                <Col md={6}>
                                    <Form.Group controlId="formDNI" className="mb-3">
                                        <Form.Label className="fw-semibold">DNI/NIE</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="dni"
                                            value={localClient.dni}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                            placeholder="Ej: 12345678A"
                                            className={`form-control-lg${dniError ? ' is-invalid' : ''}`}
                                        />
                                        {dniError && (
                                            <Form.Control.Feedback type="invalid">
                                                {dniError}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="formDateOfBirth" className="mb-3">
                                        <Form.Label className="fw-semibold">Fecha de Nacimiento</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="dateOfBirth"
                                            value={localClient.dateOfBirth ? new Date(localClient.dateOfBirth).toISOString().slice(0, 10) : ''}
                                            onChange={handleDateChange}
                                            disabled={!isEditing}
                                            className="form-control-lg"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {isEditing && (
                        <div className="d-flex gap-3 justify-content-end mt-4 pt-3 border-top">
                            <Button type="submit" variant="primary" size="lg" className="d-flex align-items-center px-4">
                                <FaSave className="me-2" />
                                Guardar Cliente
                            </Button>
                            {showCancelButton && handleCancel && (
                                <Button variant="outline-secondary" size="lg" onClick={handleCancel} className="d-flex align-items-center px-4">
                                    <FaRegWindowClose className="me-2" />
                                    Cancelar
                                </Button>
                            )}
                        </div>
                    )}
                </form>
            </Card.Body>
        </Card>
        </>
    );
};

export default ClientInfoForm;
